import { AxiosError } from 'axios';
import { getTypeInfo, types as CardType } from 'credit-card-type';
import FormattedAddress from 'features/address-book/FormattedAddress';
import React, { useState } from 'react';
import { Localizer } from 'services/Localizer';
import { CollapsibleReadMoreText } from 'shared/components/CollapsibleReadMoreText';
import ProductPricing from 'shared/components/commerce/ProductPricing';
import Spinner from 'shared/components/Spinner';
import OrganizationSimpleSVG from 'shared/components/svg/commerce/OrganizationSimpleSVG';
import CreditCardSimpleSVG from 'shared/components/svg/CreditCardSimpleSVG';
import InlineAlert from 'shared/edit/InlineAlert';
import { SupportedCardTypes } from 'shared/util/credit-card/types';
import { PriceDisplayRule } from '../../../../../Roc.SharedClientApp/types/commerce/display-price';
import {
	CreditCardType,
	//#region HCL
	//ExportStatus,
	//#endregion
	Order,
	OrderItem,
	OrderNote,
	OrderRecipient,
	PaymentLog,
	PaymentTypeEnum,
} from '../shopping-cart/types';
//#region HCL
//import OrderCancelButton from './OrderCancelButton';
//#endregion
import OrderDetailsItem from './OrderDetailsItem';
import { OrderDetailsResult } from './types';
//#region HCL
import OrderDetailsAddOnItemCustom from './OrderDetailsAddOnItem.hcl';
//#endregion

interface OrderDetailsProps {
	orderDetails: OrderDetailsResult;
}

interface NotesProps {
	note: OrderNote;
}

/**
 * Component for rendering my account order details
 */
export default function OrderDetails(props: OrderDetailsProps) {
	//#region HCL
	//const [updatedOrder, setUpdatedOrder] = useState<null | Order>(null);
	//const { error, isLoading, order: loadedOrder, isCancellable } = props.orderDetails;
	const [updatedOrder] = useState<null | Order>(null);
	const { error, isLoading, order: loadedOrder } = props.orderDetails;
	//#endregion
	const order = updatedOrder ?? loadedOrder;
	const axiosError = error as AxiosError;

	if (isLoading) {
		return <Spinner />;
	}

	if (axiosError?.response) {
		return <InlineAlert message={axiosError.response.data.message} error={axiosError} />;
	}

	if (error || !order) {
		return <InlineAlert message={Localizer('OrderDetailsError')} error={error ?? null} />;
	}

	return (
		<>
			<h1>
				{/* #region HCL */}
				{order.isP21Order ? Localizer('Order') : Localizer('OrderWebReferenceNo')}
				{(order && ' #: ' + order.orderNo) || ''}
				{/* #endregion */}
			</h1>

			{order && (
				<>
					<div className="roc-manage-layout__card">
						<div className="roc-row">
							<div className="roc-col-md-6 roc-manage-layout__card-flush">
								<h2>{Localizer('Overview')}</h2>

								<p className="roc-manage-layout__card-text">
									<strong>{Localizer('Date')}: </strong>
									{order.localizedOrderDate}
								</p>

								{/* #region HCL */}
								{order.isP21Order && order.orderStatus ? (
									<p className="roc-manage-layout__card-text">
										<strong>{Localizer('OrderStatus')}: </strong>
										{order.orderStatus}
									</p>
								) : null}

								{!order.isP21Order && order.exportStatus ? (
									<p className="roc-manage-layout__card-text">
										<strong>{Localizer('OrderStatus')}: </strong>
										{Localizer('Ordered')}
									</p>
								) : null}

								{/* #endregion */}

								{order.purchaseOrderNumber ? (
									<p className="roc-manage-layout__card-text">
										<strong>{Localizer('PONumber')}: </strong>
										{order.purchaseOrderNumber}
									</p>
								) : null}

								{order.paymentLogs?.map((paymentLog, index) => (
									<OrderPayments paymentLog={paymentLog} key={index} />
								))}

								{/*#region HCL */}
								{/*{order.isLocked || order.orderStatus === 'Canceled' || !isCancellable ? null : (
									<OrderCancelButton
										orderId={order.id}
										setUpdatedOrder={setUpdatedOrder}
										className="roc-btn--top-space"
									/>
								)}*/}
								<p className="roc-manage-layout__card-text">
									<span dangerouslySetInnerHTML={{ __html: Localizer('CancelMessage') }} />
								</p>
								<p className="roc-manage-layout__card-text">
									<strong>
										<span>
										{/* #region HCL */}
											To track your order, please visit the <a href={'/organization/orders?order_no=' + order.orderNo}>order history.</a>					
										{/* #endregion */}
										</span>
									</strong>
								</p>
								{/*#endregion */}

								{/* Hidden for now until ReOrder works */}
								{/* <button type="button" className="roc-btn roc-manage-layout__btn roc-manage-layout__btn--spaced">
									{Localizer('ReOrderAllItems')}
								</button> */}
							</div>
							<div className="roc-col-md-6">
								<OrderSummary order={order} />
							</div>
						</div>
					</div>

					{order?.recipients ? (
						<div className="roc-manage-layout__card roc-manage-layout__card-flush">
							{order.recipients.map((recipient, index) => {
								return (
									<React.Fragment key={index}>
										<RecipientAddress recipient={recipient} />
										<OrderItems items={recipient.items} />
									</React.Fragment>
								);
							})}
						</div>
					) : null}

					<div className="roc-manage-layout__card roc-manage-layout__card-flush">
						<h2 className="roc-panel__title">
							<strong>{Localizer('Notes')}</strong>
						</h2>
						{order?.notes?.length ? (
							<div className="roc-table-responsive roc-manage-layout__table-responsive">
								<table className="roc-manage-layout__table">
									<thead className="roc-manage-table__thead">
										<tr className="roc-manage-table__row">
											<th className="roc-manage-table__th">{Localizer('NoteType')}</th>
											<th className="roc-manage-table__th">{Localizer('Note')}</th>
											<th className="roc-manage-table__th">{Localizer('Date')}</th>
										</tr>
									</thead>
									<tbody className="roc-manage-table__tbody">
										{order.notes.map((note: OrderNote, index) => {
											return <Notes note={note} key={index} />;
										})}
									</tbody>
								</table>
							</div>
						) : (
							Localizer('NoNotesAvailable')
						)}
					</div>
				</>
			)}
		</>
	);
}

/**
 * Displays note
 */
function Notes(props: NotesProps) {
	const { note } = props;

	return (
		<tr className="roc-manage-table__row">
			<td className="roc-manage-table__td">{note.noteType}</td>
			<td className="roc-manage-table__td">
				<CollapsibleReadMoreText
					text={note.note}
					readLessText={Localizer('ReadLessText')}
					readMoreText={Localizer('ReadMoreText')}
				/>
			</td>
			<td className="roc-manage-table__td">{note.createDate}</td>
		</tr>
	);
}
//#region OrderPayments
/**
 * Displays payment information for the order details component.
 */
function OrderPayments(props: { paymentLog: PaymentLog }) {
	const { paymentLog } = props;

	/**
	 * Returns the credit card image from the given credit card type.
	 * Returns null if the credit card type is not supported.
	 */
	const getCardImage = (creditCardType: CreditCardType | null | undefined) => {
		let cardType: CardType;

		if (!creditCardType) {
			return null;
		}

		for (const index in SupportedCardTypes) {
			if (SupportedCardTypes[index] === creditCardType.replace('_', '-').toLowerCase()) {
				cardType = SupportedCardTypes[index];
			}
		}

		if (!cardType) {
			return null;
		}

		const typeInfo = getTypeInfo(cardType);
		return <img src={`/images/credit-card/${typeInfo.type}.jpg`} alt={typeInfo.niceType} />;
	};

	return (
		<table className="roc-manage-layout__simple-table" data-testid="field-payments">
			<tbody>
				<tr>
					<td>
						<strong>{Localizer('Payment')}: </strong>
					</td>
					<td>
						{paymentLog ? (
							<>
								{paymentLog.paymentType === PaymentTypeEnum.CreditCard ? (
									<>
										{getCardImage(paymentLog.creditCardType)} {Localizer('CardEndingIn')}
										{' ' + paymentLog.last4}
									</>
								) : paymentLog.paymentType === PaymentTypeEnum.GiftCard ? (
									<>
										<CreditCardSimpleSVG /> {Localizer('GiftCardEndingIn')}{' '}
										{paymentLog.giftCardNumber}
									</>
								) : paymentLog.paymentType === PaymentTypeEnum.CorporateAccount ? (
									<>
										<OrganizationSimpleSVG /> {Localizer('CorporateAccount')}
									</>
								) : null}
							</>
						) : null}
					</td>
				</tr>
			</tbody>
		</table>
	);
}
//#endregion OrderPayments

//#region OrderSummary
function OrderSummary(props: { order: Order }) {
	const { order } = props;
	//#region HCL
	const isPayByTerms = order.paymentTypes?.some((x) => x == PaymentTypeEnum.CorporateAccount);
	const recipient = order && order.recipients && order.recipients.length > 0 ? order.recipients[0] : null;
	//#endRegion
	return (
		<div className="roc-manage-layout__panel">
			<div className="roc-manage-layout__panel-section">
				<h2 className="roc-manage-layout__panel-heading">{Localizer('OrderSummary')}</h2>
				<div className="roc-manage-layout__panel-line-item">
					<div className="roc-manage-layout__panel-line-item-left">
						<strong>{Localizer('ItemsLabel')}</strong>
					</div>
					<div className="roc-manage-layout__panel-line-item-right">{order.totalItemsCount}</div>
				</div>
				<div className="roc-manage-layout__panel-line-item">
					<div className="roc-manage-layout__panel-line-item-left">
						<strong>{Localizer('Subtotal')}</strong>
					</div>
					<div className="roc-manage-layout__panel-line-item-right">
						<ProductPricing displayPrice={order.baseSubtotal} />
					</div>
				</div>
				{/* region HCL */}
				{recipient && !recipient.isCollectShippingMethod ? (
					<div className="roc-manage-layout__panel-line-item">
						<div className="roc-manage-layout__panel-line-item-left">
							<strong>{Localizer('Shipping')}</strong>
						</div>
						<div className="roc-manage-layout__panel-line-item-right">
							<ProductPricing displayPrice={order.shippingTotal} />
						</div>
					</div>
				) : null}
				{/* endregion */}
				<div className="roc-manage-layout__panel-line-item">
					<div className="roc-manage-layout__panel-line-item-left">
						<strong>{Localizer('EstimatedTax')}</strong>
					</div>
					<div className="roc-manage-layout__panel-line-item-right">
						<ProductPricing displayPrice={order.tax} />
					</div>
				</div>
				{order.discount?.displayRule === PriceDisplayRule.Normal &&
				order.discount?.basePrice?.rawValue !== 0 ? (
					<>
						<div className="roc-manage-layout__panel-line-item">
							<div className="roc-manage-layout__panel-line-item-left">
								<strong>{Localizer('Discount')}</strong>
							</div>
							<div className="roc-manage-layout__panel-line-item-right roc-pricing__prefix-wrapper">
								-<ProductPricing displayPrice={order.discount} />
							</div>
						</div>
					</>
				) : null}

				{order.paymentLogs?.length > 0 ? (
					<>
						{order.paymentLogs.map((paymentLog, index) =>
							paymentLog.paymentType === PaymentTypeEnum.GiftCard ? (
								<div key={index} className="roc-manage-layout__panel-line-item">
									<div className="roc-manage-layout__panel-line-item-left">
										<strong>
											{Localizer('GiftCard')} {index + 1}
										</strong>
									</div>
									<div className="roc-manage-layout__panel-line-item-right roc-pricing__prefix-wrapper">
										-
										<ProductPricing displayPrice={paymentLog.amount} />
									</div>
								</div>
							) : paymentLog.paymentType === PaymentTypeEnum.CorporateAccount ? (
								<div key={index} className="roc-manage-layout__panel-line-item">
									<div className="roc-manage-layout__panel-line-item-left">
										<strong>{Localizer('CorporateAccount')}</strong>
									</div>
									<div className="roc-manage-layout__panel-line-item-right roc-pricing__prefix-wrapper">
										-
										<ProductPricing displayPrice={paymentLog.amount} />
									</div>
								</div>
							) : null,
						)}
					</>
				) : null}
			</div>
			<div className="roc-manage-layout__panel-section">
				<div className="roc-manage-layout__panel-line-item roc-manage-layout__panel-line-item--weighted">
					<div className="roc-manage-layout__panel-line-item-left">{Localizer('Total')}</div>
					<div className="roc-manage-layout__panel-line-item-right">
						<ProductPricing
							displayPrice={isPayByTerms ? order.total : order.modifiedOrderTotal}
							className="roc-checkout-order-summary-total-price"
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
//#endregion OrderSummary

//#region RecipientAddress
function RecipientAddress(props: { recipient: OrderRecipient }) {
	const { recipient } = props;
	return (
		<>
			<h2 className="roc-panel__title">
				<strong>
					{Localizer('Shipping')}:{' '}
					{recipient.recipientName ?? (
						<>
							{recipient.firstName}
							{recipient.lastName ? ' ' + recipient.lastName : null}
						</>
					)}
					{' - ' + recipient.city}
				</strong>
			</h2>
			<div className="roc-row">
				<div className="roc-col-md-6" data-testid="shipping-address">
					<strong>{Localizer('ShippingAddress')}</strong>
					<FormattedAddress
						address={recipient}
						recipientName={recipient.recipientName}
						isOrganizationAddress={!!recipient.recipientName}
					/>
				</div>
				{recipient.shippingMethod != null ? (
					<div className="roc-col-md-6" data-testid="shipping-methods">
						<strong>{Localizer('ShippingMethod')}</strong>
						<p>{recipient.shippingMethod.name}</p>
					</div>
				) : null}
			</div>
		</>
	);
}
//#endregion RecipientAddress

//#region OrderItems
function OrderItems(props: { items: OrderItem[] }) {
	const { items } = props;

	return (
		<>
			<div className="roc-table-responsive roc-manage-layout__table-responsive">
				<table className="roc-manage-layout__table roc-manage-layout__table--consistent-backgrounds">
					<tbody>
						{items.map((item, i) => {
							return (
								<>
									<tr key={i}>
										<OrderDetailsItem item={item} key={i} index={i} />
									</tr>
									{item.orderItemAddOns?.map((addOnItem, index) => {
										return (
											<tr
												className="roc-manage-layout__table-addon-tr"
												key={index}
												data-testid={`order-item-${i}-addon-details-${index}`}
											>
												<OrderDetailsAddOnItemCustom
													addOnItem={addOnItem}
													key={index}
													addOnParentItem={item}
													index={index}
												/>
											</tr>
										);
									})}
								</>
							);
						})}
					</tbody>
				</table>
			</div>
			<p className="roc-table-responsive__scroll-message">{Localizer('ResponsiveTableScrollMessage')}</p>
		</>
	);
}
//#endregion OrderItems
